import { useCallback, useMemo } from 'react';
import { connect } from 'react-redux';

import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { IKnmErknmForm } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmForm';
import { IKnmErknmFormResultDecision } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormResultDecision';
import { booleanSelectOptions } from 'KnmPackage/MainModule/constants/booleanSelectOptions';
import { getPunishmentNature } from 'KnmPackage/MainModule/selectors/knmSelectors';
import { Button } from 'UIPackage/MainModule/components/Button';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';

import styles from './ErknmResultDecisionsItem.module.scss';
import { IKnmErknmFormAct } from '../../MainModule/interfaces/IKnmErknmFormAct';
import { ErknmResultDecisionProtocol } from './ErknmResultDecisionProtocol';
import {
  IKnmPunishmentNature,
  IKnmPunishmentNatureOption,
} from '../../../APIPackage/KnmModule/interfaces/IKnmPunishmentNature';
import { ErknmResultDecisionsResponsibleEntities } from './ErknmResultDecisionsResponsibleEntities';
import { IKnmErknmFormEvent } from '../../MainModule/interfaces/IKnmErknmFormEvent';
import { IKnmErknmFormSubject } from '../../MainModule/interfaces/IKnmErknmFormSubject';
import { KnmLateFieldsRemark } from '../../../KnmPackage/KnmLateFieldsRemarkModule/components/KnmLateFieldsRemark';
import { getTitleFieldWithRedStar } from '../../../functions/getTitleFieldWithRedStar';

interface IErknmResultDecisionsProsecutionProps {
  acts: IKnmErknmFormAct[] | undefined;
  addField: (fieldName: string) => () => void;
  eventsErknm?: IKnmErknmFormEvent[] | undefined;
  formRef: IKnmFormRef | undefined;
  id: string;
  isWatchOnly: boolean;
  lateFieldName: string;
  makeRemoveField: (fieldName: string, index: number) => () => void;
  name: string;
  organizations?: IKnmErknmFormSubject[] | undefined;
  value: IKnmErknmFormResultDecision;
}

interface IErknmResultDecisionsProsecutionStoreProps {
  punishmentNature: IKnmPunishmentNature[];
}

const ErknmResultDecisionsProsecutionWithoutStore = ({
  acts,
  addField,
  eventsErknm,
  formRef,
  id,
  isWatchOnly,
  lateFieldName,
  makeRemoveField,
  name,
  organizations,
  value,
  punishmentNature,
}: IErknmResultDecisionsProsecutionProps & IErknmResultDecisionsProsecutionStoreProps) => {
  const { kindDecision } = value;
  const kindDecisionCode = kindDecision?.code;
  const responsibleEntitiesBlockId = `${id}.responsibleEntities`;

  const handleIsProsecutionChange = useCallback(() => {
    formRef?.current.change(`${name}.responsibleEntities` as keyof IKnmErknmForm);
  }, [formRef, name]);

  /**
   * Определяем, надо ли показывать поле с выбором характера наказания
   */
  const punishmentNatureSelectorOptions = useMemo(() => {
    if (!kindDecisionCode) {
      return [];
    }

    return punishmentNature
      .filter(i => i.kindResultDecisionDTO.code === kindDecisionCode)
      .map(item => {
        return {
          id: item.id,
          value: item,
          label: item.name,
        } as IKnmPunishmentNatureOption;
      });
  }, [punishmentNature, kindDecisionCode]);

  return (
    <Row
      title={getTitleFieldWithRedStar({
        label: 'Сведения о привлечении к ответственности по результатам контрольных (надзорных) мероприятий',
        field: `${name}.isProsecution`,
        isKnm: true,
      })}
    >
      <Row notWrap notSpace>
        <FieldBlock size="col-6" id={`${id}.isProsecution`}>
          <SelectInputField
            className={styles.IsProsecutionField}
            name={`${name}.isProsecution`}
            options={booleanSelectOptions}
            size="medium"
            disabled={isWatchOnly}
            isClearable={false}
            onChange={handleIsProsecutionChange}
          />
        </FieldBlock>
        <KnmLateFieldsRemark
          lateFieldPath={`${lateFieldName}/isProsecution`}
          fieldId={`${id}.isProsecution`}
          isLeftPosition={true}
        />
      </Row>

      {value.isProsecution?.value && (
        <ErknmResultDecisionProtocol
          isWatchOnly={isWatchOnly}
          name={`${name}.protocol.attachments`}
          id={`${id}.protocol.attachments`}
          attachmentsNumber={value.protocol?.attachments?.length || 0}
          isRequired={false}
          formRef={formRef}
          lateFieldName={
            value.protocol?.attachments && value.protocol?.attachments[0]?.guid
              ? `${lateFieldName}/isProsecutionProtocol/attachments/${value.protocol?.attachments[0]?.guid}/fileName`
              : undefined
          }
        />
      )}

      {punishmentNatureSelectorOptions.length > 0 && value.isProsecution?.value && (
        <Row>
          <FieldBlock
            label={<>Характер наказания</>}
            id={`${id}.punishmentNature`}
            tooltip={
              <KnmLateFieldsRemark
                lateFieldPath={`${lateFieldName}/punishmentNature`}
                fieldId={`${id}.punishmentNature`}
              />
            }
          >
            <SelectInputField
              name={`${name}.punishmentNature`}
              placeholder="Выберите характер наказания"
              options={punishmentNatureSelectorOptions}
              disabled={isWatchOnly}
              size="medium"
            />
          </FieldBlock>
        </Row>
      )}

      {value.isProsecution?.value && (
        <>
          <FieldBlock
            label={getTitleFieldWithRedStar({
              label:
                'Сведения о привлеченных к ответственности лицах \n (включая индивидуальных предпринимателей, юридических лиц, должностных лиц)',
              field: `${name}.responsibleEntities`,
              isKnm: true,
            })}
            id={responsibleEntitiesBlockId}
            button={
              <Button
                className="shared-btn-add"
                text="Добавить"
                type="button"
                variant="link"
                color="primary"
                disabled={isWatchOnly}
                onClick={addField('responsibleEntities')}
              />
            }
            tooltip={<KnmLateFieldsRemark lateFieldPath={`${lateFieldName}/responsibleEntities`} />}
          >
            <ErknmResultDecisionsResponsibleEntities
              acts={acts}
              formRef={formRef}
              id={id}
              name={name}
              value={value}
              eventsErknm={eventsErknm}
              organizations={organizations}
              isWatchOnly={isWatchOnly}
              addField={addField}
              makeRemoveField={makeRemoveField}
              lateFieldName={`${lateFieldName}/responsibleEntities`}
            />
          </FieldBlock>
        </>
      )}
    </Row>
  );
};

const mapStateToProps = (state: IRootState): IErknmResultDecisionsProsecutionStoreProps => ({
  punishmentNature: getPunishmentNature(state),
});

export const ErknmResultDecisionsProsecution = connect(mapStateToProps)(ErknmResultDecisionsProsecutionWithoutStore);
